/**
 * Cmd/Ctrl+K 端侧论据检索浮层（Raycast / Linear 风格 Optical 版）。
 *
 * 交互保持不变：自动聚焦、↑↓ 选择、Enter 查看详情、Esc 关闭；结果只展示不跳转。
 * 视觉：75% 透镜命令面板 + 发丝分隔 + 冷白高亮行，不用彩色实心胶囊。
 */
import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { SearchHit } from '../../engines/contracts.ts';
import type { SearchState } from '../../state/types.ts';
import { Badge, cx, KeyHint } from '../ui/primitives.tsx';
import { CornerReticles } from '../system/Reticles.tsx';

const KIND_LABEL: Record<SearchHit['kind'], string> = {
  EVIDENCE: '权威论据',
  CARD: '策略卡',
  SANDBOX: '沙盘',
};

const KIND_TONE: Record<SearchHit['kind'], 'pass' | 'blind' | 'warning'> = {
  EVIDENCE: 'pass',
  CARD: 'blind',
  SANDBOX: 'warning',
};

export interface SearchOverlayProps {
  search: SearchState;
  selfSideLabel?: string;
  onChangeQuery: (query: string) => void;
  onMoveSelection: (delta: number) => void;
  onClose: () => void;
}

export function SearchOverlay({
  search,
  selfSideLabel,
  onChangeQuery,
  onMoveSelection,
  onClose,
}: SearchOverlayProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [detail, setDetail] = useState<SearchHit | null>(null);
  const hits = search.hits;
  const activeHit = hits[search.activeIndex] ?? hits[0];

  useEffect(() => {
    if (search.open) inputRef.current?.focus();
  }, [search.open]);

  useEffect(() => {
    setDetail(null);
  }, [search.query]);

  if (!search.open) return null;

  const trimmed = search.query.trim();

  const selectIndex = (index: number) => {
    if (hits.length === 0) return;
    onMoveSelection(index - search.activeIndex);
    const hit = hits[index];
    if (hit) setDetail(hit);
  };

  const onInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        onMoveSelection(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        onMoveSelection(-1);
        break;
      case 'Enter':
        event.preventDefault();
        if (activeHit) setDetail(activeHit);
        break;
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
      default:
        break;
    }
  };

  return (
    <div
      data-testid="search-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="论据检索"
      className="fixed inset-0 z-50 flex items-start justify-center bg-[#020305]/78 px-4 py-20 backdrop-blur-md"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="lens lens-active spotlight-card w-full max-w-2xl overflow-hidden">
        <CornerReticles inset={10} color="rgba(255,255,255,0.3)" />
        <div className="flex items-center gap-3 px-5 py-4">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" className="shrink-0 text-slate-400" aria-hidden="true">
            <circle cx="11" cy="11" r="7.2" stroke="currentColor" strokeWidth="1.4" />
            <path d="m16.6 16.6 4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          <input
            ref={inputRef}
            data-testid="search-input"
            type="search"
            value={search.query}
            autoFocus
            placeholder="检索论据 / 策略卡，例如：陶哲轩、最高法、81%"
            onChange={(event) => onChangeQuery(event.target.value)}
            onKeyDown={onInputKeyDown}
            className="min-w-0 flex-1 bg-transparent text-[14px] text-slate-100 outline-none placeholder:text-slate-600"
          />
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded bg-white/[0.04] px-2 py-1 font-mono text-[10px] text-slate-400 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.1),inset_0_0_0_1px_rgba(255,255,255,0.04)] transition hover:text-slate-100"
            data-testid="search-close"
          >
            ESC
          </button>
        </div>
        <div className="hairline" />
        <div className="flex flex-wrap items-center gap-2 px-5 py-2 text-[10px] text-slate-500">
          <KeyHint>↑</KeyHint>
          <KeyHint>↓</KeyHint>
          <span>选择</span>
          <KeyHint>Enter</KeyHint>
          <span>详情</span>
          <span className="ml-auto flex items-center gap-2">
            <Badge tone="blind">MiniSearch BM25</Badge>
            <span className="mono">零网络 · 端侧</span>
            {selfSideLabel && <span className="text-slate-600">视角：{selfSideLabel}</span>}
          </span>
        </div>
        <div className="hairline" />

        <div className="max-h-[46vh] overflow-y-auto" data-testid="search-results">
          {hits.length === 0 ? (
            <div className="px-6 py-10 text-center" data-testid="search-empty">
              {trimmed ? (
                <>
                  <p className="text-[13px] text-slate-300">没有匹配「{trimmed}」的论据</p>
                  <p className="mt-1.5 text-[11px] text-slate-500">
                    尝试更短的关键词：陶哲轩 / 最高法 / 闭卷 / 81% / 核验
                  </p>
                </>
              ) : (
                <>
                  <p className="text-[13px] text-slate-300">输入关键词开始检索</p>
                  <p className="mt-1.5 text-[11px] text-slate-500">
                    示例：陶哲轩（工具观）/ 最高法（核验义务）/ 81%（外包率口径）
                  </p>
                </>
              )}
            </div>
          ) : (
            <ul>
              {hits.map((hit, index) => {
                const isActive = index === search.activeIndex;
                return (
                  <li key={`${hit.kind}-${hit.id}`} className={cx(index > 0 && 'border-t border-white/[0.045]')}>
                    <button
                      type="button"
                      data-testid="search-result"
                      data-hit-id={hit.id}
                      data-active={isActive ? 'true' : 'false'}
                      onClick={() => selectIndex(index)}
                      className={cx(
                        'relative flex w-full items-start gap-3 px-5 py-3 text-left transition-colors',
                        isActive ? 'bg-white/[0.055]' : 'hover:bg-white/[0.03]',
                      )}
                    >
                      {isActive && (
                        <span className="absolute inset-y-2 left-0 w-px bg-gradient-to-b from-white/80 via-white/40 to-transparent shadow-[0_0_10px_rgba(255,255,255,0.5)]" />
                      )}
                      <span className="mt-0.5 shrink-0">
                        <Badge tone={KIND_TONE[hit.kind]}>{KIND_LABEL[hit.kind]}</Badge>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[12.5px] font-medium text-slate-100">
                          {hit.title}
                        </span>
                        <span className="clamp-3 mt-1 block text-[11px] leading-relaxed text-slate-400">
                          {hit.text}
                        </span>
                        {hit.matchedTerms.length > 0 && (
                          <span className="mt-1.5 flex flex-wrap gap-1">
                            {hit.matchedTerms.slice(0, 4).map((term) => (
                              <span key={term} className="chip font-mono">
                                {term}
                              </span>
                            ))}
                          </span>
                        )}
                      </span>
                      <span className="mono shrink-0 text-[9px] text-slate-600">{hit.score.toFixed(1)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {detail && (
          <div data-testid="search-detail" className="border-t border-white/[0.06] bg-white/[0.015] px-5 py-4">
            <div className="flex items-center gap-2">
              <Badge tone={KIND_TONE[detail.kind]}>{KIND_LABEL[detail.kind]}</Badge>
              <span className="text-[12.5px] font-medium text-slate-100">{detail.title}</span>
              <span className="micro-label ml-auto">仅速查 · 不跳转</span>
            </div>
            <p className="mt-2 text-[12px] leading-relaxed text-slate-200">{detail.text}</p>
            {detail.tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {detail.tags.map((tag) => (
                  <span key={tag} className="chip">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
